import Select from "@/components/select"
import { useFormContext } from "@/lib/form/form-context"
import { DropdownParamSchema } from "@/lib/form/schema"
import { FormParamStatus, WidgetBaseProps } from "@/lib/form/types"
import { useWatch } from "@/lib/form/form"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import Combobox from "@/components/ui/combobox"

export interface DropdownWidgetProps
  extends WidgetBaseProps<DropdownParamSchema> {
  className?: string
}

export default function DropdownWidget(props: DropdownWidgetProps) {
  const { schema, className, path } = props
  const { form, readOnly: ctxReadOnly, sheetHasActiveBreakdown } = useFormContext()

  const value = useWatch({
    form: form,
    name: path,
    deep: 1,
  })
  const selectedOption =
    schema.options.find((option) => option.value === value?.value) || null

  function handleChange(value: string) {
    form.setValue(
      path,
      {
        id: schema.id,
        value: value,
        status: FormParamStatus.DRAFT,
        recordedAt: new Date().toISOString(),
      },
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )
  }

  function getPreviewValue(): string {
    const hasSelectedValue = !!selectedOption?.label

    if (value?.status === FormParamStatus.NOT_APPLICABLE) {
      return "NA"
    }

    if (!hasSelectedValue && sheetHasActiveBreakdown) {
      return "NA"
    }

    if (!hasSelectedValue) {
      return "PENDING"
    }

    return selectedOption?.label || ""
  }

  if (value?.status && value?.status !== FormParamStatus.DRAFT) {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        <Label>{schema.name}</Label>

        <p className="text-sm font-medium">{getPreviewValue()}</p>
      </div>
    )
  }

  return (
    <>
      {schema.name === "Vendor Name" ? (
        <Combobox
          className={className}
          label="Vendor Name"
          options={schema.options}
          defaultValue={[value?.value]}
          multiple={false}
          disabled={
            value?.status === FormParamStatus.DISABLED ||
            !!ctxReadOnly ||
            !!sheetHasActiveBreakdown
          }
          onChange={(selected) => {
            const selectedValue = selected[0]
            const selectedOption = schema.options.find(
              (opt) => opt.value === selectedValue
            )
            if (selectedOption) {
              handleChange(selectedValue)
            }
          }}
        />
      ) : (
        <Select
          className={className}
          label={schema.name}
          options={schema.options}
          value={value?.value}
          onChange={(selectedValue) => handleChange(selectedValue)}
          disabled={
            value?.status === FormParamStatus.DISABLED ||
            !!ctxReadOnly ||
            !!sheetHasActiveBreakdown
          }
        />
      )}
    </>
  )
}
